/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useState, useMemo, useEffect } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { DataShareCreateBasic, DataShareMin } from '@g17eco/types/dataShare';
import { DataShareForm, shareScopeViews } from './DataShareForm';
import { useSiteAlert } from '@hooks/useSiteAlert';
import {
  detectChangesDataShareRequest,
  getDefaultRequestDataScope,
  getScopeViews,
  getScopeModules,
  combinedScope,
  mergeScopeViews,
  getScopeAccess,
  combinedScopeAccess,
  isRestricted,
} from '@utils/dataShare';
import { useGetMetricGroupsQuery } from '@api/metric-groups';
import { Loader } from '@g17eco/atoms/loader';
import { useCreateDataShareRequestMutation } from '@api/data-share';
import { BasicAlert } from '@g17eco/molecules/alert';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { DataScope, DataScopeAccess } from '../../types/surveyDataScope';

/*
  Request Data share logic:
  - All toggle ON, in new share request
  - Disabled "Send" when no changes to request
  - Preset value and automatically switch ON when all fields are selected
  - Disabled "Send" when deselect everything except Messaging toggle
  - Display “CMP:” for custom metric modules
  - Modules belongs to sponsorship are visible, selected, and disabled
 */

const UNCHANGE_TOOLTIP = 'Data or view access must be selected to request sharing';

interface DataShareRequestForm extends DataShareCreateBasic {
  dataScope: DataScope;
}

const validateDataShareRequest = (request: DataShareRequestForm) => {
  const hasTitle = request.title.length > 0;
  if (!hasTitle) {
    return false;
  }

  // invalid when all scopes and scope views are not selected
  const scopeModules = getScopeModules(request.dataScope);
  const access = getScopeAccess(request.dataScope);
  const scopeViews = getScopeViews(request.dataScope);
  if (
    access === DataScopeAccess.Partial &&
    scopeModules.length === 0 &&
    shareScopeViews.every((v) => !scopeViews.includes(v))
  ) {
    return false;
  }

  return true;
};

interface Props extends DataShareCreateBasic {
  initiativeName: string;
  isOpen: boolean;
  toggle: (update?: { reload?: boolean }) => void;
  acceptedDataShares?: DataShareMin[];
}

interface MessageTextProps {
  requesterName: string;
}

export const getDefaultContent = ({ requesterName }: MessageTextProps) => {
  return `G17Eco Portfolio Tracker user ${requesterName} has requested access to your company’s sustainability reporting data.`;
};

export const DataShareModal = (props: Props) => {
  const {
    requesterId,
    initiativeId,
    initiativeName,
    requesterType,
    title,
    content,
    acceptedDataShares = [],
    isOpen,
    toggle,
  } = props;

  const { addSiteAlert } = useSiteAlert();

  const { data: customMetricGroups, isFetching } = useGetMetricGroupsQuery(requesterId);
  const [createDataShareRequest, { isLoading, error }] = useCreateDataShareRequestMutation();

  const dataScope = useMemo<DataScope | undefined>(() => {
    if (!acceptedDataShares.length) {
      return undefined;
    }
    return { survey: { scope: combinedScope(acceptedDataShares), views: mergeScopeViews(acceptedDataShares), access: combinedScopeAccess(acceptedDataShares) } };
  }, [acceptedDataShares]);

  const restrictedScopes = useMemo<string[]>(() => {
    if (!acceptedDataShares.length) {
      return [];
    }
    const restrictedDataShares = acceptedDataShares.filter(dataShare => isRestricted(dataShare));
    const combinedRestrictedScope = combinedScope(restrictedDataShares);
    const modules = Object.values(combinedRestrictedScope).map(items => items).flat();
    return modules;
  }, [acceptedDataShares])

  const [form, setForm] = useState<DataShareRequestForm>({
    requesterId,
    requesterType,
    initiativeId,
    title,
    content,
    dataScope: getDefaultRequestDataScope(),
  });

  const hasChangesDataShareRequest = detectChangesDataShareRequest(dataScope, form.dataScope);
  const validDataShareRequest = validateDataShareRequest(form);

  useEffect(() => {
    if (dataScope) {
      setForm((prev) => ({ ...prev, dataScope }));
    }
  }, [dataScope]);

  const hasTitle = form.title.length > 0;
  const disabledSubmit = isLoading || isFetching || !hasTitle || !hasChangesDataShareRequest || !validDataShareRequest;

  const onSubmit = () => {
    if (disabledSubmit) {
      return;
    }

    createDataShareRequest(form)
      .unwrap()
      .then(() => {
        addSiteAlert({
          content: `Your share request to ${initiativeName} has been sent.`,
          timeout: 5000,
        });
        toggle({ reload: true });
      });
  };

  const onChange = (u: Partial<DataShareCreateBasic>) => setForm((f) => ({ ...f, ...u }));

  return (
    <Modal isOpen={isOpen} toggle={() => toggle()} backdrop='static'>
      <ModalHeader className='py-3 border-0' toggle={() => toggle()}>
        Data share request
      </ModalHeader>
      <ModalBody className='py-0'>
        {isFetching || isLoading ? <Loader /> : null}
        <div className='pt-3 border-top border-ThemeBorderDefault'>
          {error?.message ? <BasicAlert type={'danger'}>{error.message}</BasicAlert> : null}
          <DataShareForm
            form={form}
            onChange={onChange}
            disabled={isLoading}
            disabledContent={Boolean(dataScope)}
            customMetricGroups={customMetricGroups}
            restrictedScopes={restrictedScopes}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color='link-secondary' disabled={isLoading} onClick={() => toggle()}>
          <span>Cancel</span>
        </Button>
        <SimpleTooltip text={!validDataShareRequest ? UNCHANGE_TOOLTIP : undefined}>
          <Button color='primary' disabled={disabledSubmit} onClick={onSubmit}>
            Send
          </Button>
        </SimpleTooltip>
      </ModalFooter>
    </Modal>
  );
};
